import { AITaskRequest, AITaskResponse, AITaskType } from '@types/index'
import apiClient from './client'

export const aiAPI = {
  // Process AI task
  async processTask(request: AITaskRequest, type: AITaskType): Promise<AITaskResponse> {
    return apiClient.post(`/api/ai-tasks/${type}`, request)
  },

  // Proofread text
  async proofread(text: string, context?: string): Promise<AITaskResponse> {
    return this.processTask({ text, context }, 'proofread')
  },

  // Rewrite text
  async rewrite(text: string, instructions?: string, context?: string): Promise<AITaskResponse> {
    return this.processTask({ text, instructions, context }, 'rewrite')
  },

  // Expand text
  async expand(text: string, instructions?: string, context?: string): Promise<AITaskResponse> {
    return this.processTask({ text, instructions, context }, 'expand')
  },

  // Generate text
  async generate(prompt: string, instructions?: string, context?: string): Promise<AITaskResponse> {
    return this.processTask({ text: prompt, instructions, context }, 'generate')
  },

  // Generate LaTeX table from natural language or data
  async generateTable(
    description: string,
    images?: string[],
    context?: string,
    documentId?: string
  ): Promise<AITaskResponse> {
    return this.processTask(
      {
        text: description,
        images,
        context,
        instructions: 'Generate a LaTeX table based on the description. Return only valid LaTeX table code without any explanations or markdown formatting.',
        document_id: documentId
      },
      'generate-table'
    )
  },

  // Generate LaTeX equation from natural language or image
  async generateEquation(
    description: string,
    images?: string[],
    context?: string,
    documentId?: string
  ): Promise<AITaskResponse> {
    return this.processTask(
      {
        text: description,
        images,
        context,
        instructions: 'Generate a LaTeX equation based on the description. Return only valid LaTeX math code without any explanations or markdown formatting.',
        document_id: documentId
      },
      'generate-equation'
    )
  },

  // Batch processing
  async processBatch(requests: Array<{ request: AITaskRequest; type: AITaskType }>): Promise<AITaskResponse[]> {
    return Promise.all(
      requests.map(({ request, type }) => this.processTask(request, type))
    )
  },

  // Get task suggestions based on text
  async getTaskSuggestions(text: string): Promise<{
    suggestions: Array<{
      type: AITaskType
      confidence: number
      reason: string
    }>
  }> {
    // Mock implementation - in real app, this would be an API call
    const suggestions = []
    
    // Suggest proofreading if text has potential errors
    if (/\b(teh|recieve|occured|seperate)\b/i.test(text)) {
      suggestions.push({
        type: 'proofread' as AITaskType,
        confidence: 0.9,
        reason: 'Potential spelling errors detected'
      })
    }
    
    // Suggest rewriting if text has passive voice or complex sentences
    if (/\b(was|were|been|being)\s+\w+ed\b/i.test(text) || text.split(' ').length > 30) {
      suggestions.push({
        type: 'rewrite' as AITaskType,
        confidence: 0.7,
        reason: 'Text could be clearer or more concise'
      })
    }
    
    // Suggest expanding if text is very short
    if (text.split(' ').length < 10) {
      suggestions.push({
        type: 'expand' as AITaskType,
        confidence: 0.6,
        reason: 'Text could benefit from more detail'
      })
    }
    
    return { suggestions }
  },

  // Analyze text for insights
  async analyzeText(text: string): Promise<{
    readability_score: number
    sentiment: 'positive' | 'negative' | 'neutral'
    key_concepts: string[]
    suggestions: string[]
  }> {
    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    const words = text.split(/\s+/).length
    const sentences = text.split(/[.!?]+/).length
    const avgWordsPerSentence = words / sentences
    
    // Simple readability calculation
    const readabilityScore = Math.max(0, Math.min(100, 100 - (avgWordsPerSentence * 2)))
    
    // Extract potential key concepts (simple word frequency)
    const wordCounts = text.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(word => word.length > 4)
      .reduce((acc, word) => {
        acc[word] = (acc[word] || 0) + 1
        return acc
      }, {} as Record<string, number>)
    
    const keyConcepts = Object.entries(wordCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([word]) => word)
    
    return {
      readability_score: Math.round(readabilityScore),
      sentiment: 'neutral',
      key_concepts: keyConcepts,
      suggestions: [
        'Consider breaking long sentences into shorter ones',
        'Add more specific examples to support your points',
        'Use active voice where possible'
      ]
    }
  },

  // Get AI model capabilities
  async getCapabilities(): Promise<{
    models: Array<{
      provider: 'openai' | 'anthropic'
      name: string
      description: string
      max_tokens: number
      supports: AITaskType[]
    }>
    current_model: string
  }> {
    return {
      models: [
        {
          provider: 'openai',
          name: 'gpt-3.5-turbo',
          description: 'Fast and efficient for most tasks',
          max_tokens: 4096,
          supports: ['proofread', 'rewrite', 'expand', 'generate']
        },
        {
          provider: 'openai',
          name: 'gpt-4',
          description: 'Most capable model for complex tasks',
          max_tokens: 8192,
          supports: ['proofread', 'rewrite', 'expand', 'generate']
        },
        {
          provider: 'anthropic',
          name: 'claude-3-sonnet',
          description: 'Balanced performance and capability',
          max_tokens: 4096,
          supports: ['proofread', 'rewrite', 'expand', 'generate']
        }
      ],
      current_model: 'gpt-3.5-turbo'
    }
  },
}